import {
  DEFAULT_DATE_PRESET, isDatePresetKey, type DatePresetKey,
} from './date-presets';
import {
  DEFAULT_DRILLDOWN_GROUP_BY,
  DEFAULT_DRILLDOWN_GROUP_BY_2, DEFAULT_DRILLDOWN_GROUP_BY_3, DEFAULT_GROUP_BY, isLevelValue,
} from '../constants/tracking-groupings';
import { DEFAULT_TZ_OFFSET, TIMEZONE_OPTIONS } from './timezone';
// type-only: keeps the dependency one-way (no cycle)
import type { TrackingPreset } from './tracking-presets';

/**
 * Per-user report defaults. Every field is optional — unset means "no preference"
 * and the report falls back to the shared default.
 */
export interface TrackingDefaults {
  date_preset?: DatePresetKey;
  /** Fixed UTC offset in hours, matching the Timezone selector. */
  timezone?: number;
  group_by?: string;
  drilldown_group_by?: string;
  drilldown_group_by_2?: string;
  drilldown_group_by_3?: string;
  order_by?: string;
  order?: 'asc' | 'desc';
  /**
   * Metric columns to show, in display order. Unset means every column, in its natural
   * order. Columns the report pins (the group name, the profitability strip, the drilldown
   * toggle) are not listed here — hiding those would leave rows unidentifiable.
   */
  columns?: string[];
  /** Switched off explicitly. Absent means visible, so a new column is never hidden by default. */
  hidden_columns?: string[];
  clicks_columns?: string[];
  clicks_hidden_columns?: string[];
  conversions_columns?: string[];
  conversions_hidden_columns?: string[];
  /** Excludes known crawlers from visits and every metric derived from them. */
  exclude_bots?: boolean;
  /** Credit a conversion to its originating click's time rather than its own. */
  conversions_by_click?: boolean;
  /** Absent = shown: only an explicit false hides the admin workspace strip. */
  show_workspace_strip?: boolean;
}

/** One key per feature area. */
export interface UserSettings {
  tracking?: TrackingDefaults;
  presets?: TrackingPreset[];
  theme_mode?: 'system' | 'light' | 'dark' | 'night';
}

export interface ResolvedTrackingDefaults {
  datePreset: DatePresetKey;
  timezone: number;
  groupBy: string;
  drilldownGroupBy: string;
  drilldownGroupBy2: string;
  drilldownGroupBy3: string;
  orderBy: string;
  order: 'asc' | 'desc';
  excludeBots: boolean;
  conversionsByClick: boolean;
}

export const SORTABLE_FIELDS: { value: string; label: string }[] = [
  { value: 'visits', label: 'Visits' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'unique_visits', label: 'Unique visits' },
  { value: 'unique_clicks', label: 'Unique clicks' },
  { value: 'conversions', label: 'Conversions' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'cost', label: 'Cost' },
  { value: 'profit', label: 'Profit' },
  { value: 'roas', label: 'ROAS' },
  { value: 'cr', label: 'CR' },
  { value: 'ctr', label: 'CTR' },
  { value: 'cv_rate', label: 'CV rate' },
  { value: 'epv', label: 'EPV' },
  { value: 'cpv', label: 'CPV' },
  { value: 'rpl', label: 'RPL' },
  { value: 'cpl', label: 'CPL' },
  { value: 'bot_visits', label: 'Bot visits' },
];

const DEFAULT_ORDER_BY = 'name';

function isTimezoneOffset(v: unknown): v is number {
  return typeof v === 'number' && TIMEZONE_OPTIONS.some((o) => o.value === v);
}

/**
 * Each field validates and falls back independently: a preference naming a
 * grouping that was since removed must not also discard the user's timezone.
 */
export function resolveTrackingDefaults(settings: UserSettings | null | undefined): ResolvedTrackingDefaults {
  const t = settings?.tracking;
  return {
    datePreset: isDatePresetKey(t?.date_preset) ? t!.date_preset! : DEFAULT_DATE_PRESET,
    timezone: isTimezoneOffset(t?.timezone) ? t!.timezone! : DEFAULT_TZ_OFFSET,
    groupBy: isLevelValue(t?.group_by) ? t!.group_by! : DEFAULT_GROUP_BY,
    drilldownGroupBy: isLevelValue(t?.drilldown_group_by)
      ? t!.drilldown_group_by!
      : DEFAULT_DRILLDOWN_GROUP_BY,
    drilldownGroupBy2: isLevelValue(t?.drilldown_group_by_2)
      ? t!.drilldown_group_by_2!
      : DEFAULT_DRILLDOWN_GROUP_BY_2,
    drilldownGroupBy3: isLevelValue(t?.drilldown_group_by_3)
      ? t!.drilldown_group_by_3!
      : DEFAULT_DRILLDOWN_GROUP_BY_3,
    orderBy: SORTABLE_FIELDS.some((f) => f.value === t?.order_by) ? t!.order_by! : DEFAULT_ORDER_BY,
    order: t?.order === 'asc' || t?.order === 'desc' ? t.order : 'desc',
    // Bots excluded unless the user says otherwise: the numbers are wrong the other way.
    excludeBots: typeof t?.exclude_bots === 'boolean' ? t.exclude_bots : true,
    conversionsByClick: t?.conversions_by_click === true,
  };
}

/** Truncate at the first unset level: a "None" mid-chain stops it, and a later level can't jump the gap. */
export function initialDrilldownChain(d: ResolvedTrackingDefaults): string[] {
  const chain: string[] = [];
  for (const level of [d.drilldownGroupBy, d.drilldownGroupBy2, d.drilldownGroupBy3]) {
    if (!level) break;
    chain.push(level);
  }
  return chain;
}
